"use client";

import type { ConceptSpec } from "../spec";
import { Arrow, Dim, Plate, Sheet, SheetHeading, TitleBlock } from "./drawing-kit";

const REGION_CSS = `
.drw .dvr-region-card{fill:var(--ground);fill-opacity:.88;stroke:var(--ink);stroke-opacity:.42;stroke-width:1.1;}
.drw .dvr-region-card.selected{stroke:var(--dark);stroke-opacity:1;stroke-width:2.2;}
.drw .dvr-selection-band{fill:var(--dark);}
.drw .dvr-selection-text{fill:var(--ground);font-size:9px;font-weight:600;letter-spacing:.18em;text-transform:uppercase;}
.drw .dvr-boundary{fill:var(--ground);fill-opacity:.4;stroke:var(--dark);stroke-opacity:.9;stroke-width:2;stroke-dasharray:8 5;}
.drw .dvr-zone{fill:var(--ink);fill-opacity:.035;stroke:var(--ink);stroke-opacity:.4;stroke-width:1.1;}
.drw .dvr-zone.external{stroke-dasharray:5 5;}
.drw .dvr-twin-shell{fill:var(--dark);stroke:var(--dark);stroke-width:1.5;}
.drw .dvr-twin-title{fill:var(--ground);font-size:16px;font-weight:600;letter-spacing:.12em;text-transform:uppercase;}
.drw .dvr-twin-copy{fill:var(--ground);fill-opacity:.86;font-size:9px;letter-spacing:.12em;text-transform:uppercase;}
.drw .dvr-status-dot{fill:var(--dark);}
`;

const EU_STAR_POSITIONS = [
  { x: 50, y: 16.33 },
  { x: 58.5, y: 18.61 },
  { x: 64.72, y: 24.83 },
  { x: 67, y: 33.33 },
  { x: 64.72, y: 41.83 },
  { x: 58.5, y: 48.05 },
  { x: 50, y: 50.33 },
  { x: 41.5, y: 48.05 },
  { x: 35.28, y: 41.83 },
  { x: 33, y: 33.33 },
  { x: 35.28, y: 24.83 },
  { x: 41.5, y: 18.61 },
] as const;

const EU_STAR_POINTS = "0,-2.8 0.82,-0.88 2.85,-0.88 1.18,0.36 1.76,2.4 0,1.2 -1.76,2.4 -1.18,0.36 -2.85,-0.88 -0.82,-0.88";

const US_STRIPES = Array.from({ length: 13 }, (_, index) => index);
const US_STAR_POINTS = Array.from({ length: 9 }, (_, row) => {
  const count = row % 2 === 0 ? 6 : 5;
  return Array.from({ length: count }, (_, column) => ({
    x: ((column + 1) / (count + 1)) * 40,
    y: ((row + 1) / 10) * (7 / 13) * 100,
  }));
}).flat();

type FlagProps = {
  x: number;
  y: number;
  width: number;
  height: number;
  opacity?: number;
};

export function EuFlag({ x, y, width, height, opacity = 1 }: FlagProps) {
  return (
    <g aria-hidden="true" opacity={opacity} transform={`translate(${x} ${y}) scale(${width / 100} ${height / 66.67})`}>
      <rect width="100" height="66.67" rx="1.5" fill="#003399" />
      {EU_STAR_POSITIONS.map((star, index) => (
        <polygon
          key={`eu-star-${index}`}
          points={EU_STAR_POINTS}
          fill="#FFCC00"
          transform={`translate(${star.x} ${star.y})`}
        />
      ))}
      <rect width="100" height="66.67" rx="1.5" fill="none" stroke="#001F5B" strokeWidth="1" />
    </g>
  );
}

export function UsFlag({ x, y, width, height, opacity = 1 }: FlagProps) {
  return (
    <g aria-hidden="true" opacity={opacity} transform={`translate(${x} ${y}) scale(${width / 100} ${height / 52.63})`}>
      <rect width="100" height="52.63" fill="#FFFFFF" />
      {US_STRIPES.map((stripe) =>
        stripe % 2 === 0 ? (
          <rect key={`us-stripe-${stripe}`} y={(stripe * 52.63) / 13} width="100" height={52.63 / 13} fill="#B22234" />
        ) : null,
      )}
      <rect width="40" height={(7 * 52.63) / 13} fill="#3C3B6E" />
      {US_STAR_POINTS.map((star, index) => (
        <circle key={`us-star-${index}`} cx={star.x} cy={(star.y * 52.63) / 100} r="0.9" fill="#FFFFFF" />
      ))}
      <rect width="100" height="52.63" fill="none" stroke="#6B1B26" strokeWidth="0.8" />
    </g>
  );
}

/**
 * Union Jack in the same simplified idiom as the EU and US marks: field,
 * diagonals, cross, hairline border. Meant to sit inside an <svg> whose
 * viewBox clips the diagonal stroke ends at the corners.
 */
export function UkFlag({ x, y, width, height, opacity = 1 }: FlagProps) {
  return (
    <g
      aria-hidden="true"
      opacity={opacity}
      transform={`translate(${x} ${y}) scale(${width / 100} ${height / 66.67})`}
    >
      <rect width="100" height="66.67" rx="1.5" fill="#012169" />
      <path d="M0 0 L100 66.67 M100 0 L0 66.67" stroke="#FFFFFF" strokeWidth="12" />
      <path d="M0 0 L100 66.67 M100 0 L0 66.67" stroke="#C8102E" strokeWidth="4.5" />
      <path d="M50 0 V66.67 M0 33.33 H100" stroke="#FFFFFF" strokeWidth="20" />
      <path d="M50 0 V66.67 M0 33.33 H100" stroke="#C8102E" strokeWidth="12" />
      <rect width="100" height="66.67" rx="1.5" fill="none" stroke="#001A4D" strokeWidth="1" />
    </g>
  );
}

function RegionStampStudy() {
  return (
    <Plate
      figure="Fig. DVR-003"
      caption="A direct geography stamp makes the server-region choice legible at a glance, while keeping each flag scoped to the MLX server rather than every external route."
      motion={false}
    >
      <style>{REGION_CSS}</style>
      <Sheet
        height={620}
        label="Region stamp study for DRW-049 showing MLX server region, choose one EU or US, with the EU flag selected and the US flag presented as the available alternative; a selection readout states that the flag describes server geography only"
      >
        <SheetHeading label="Region selection stamps" sub="DVR-003 · Variant of DRW-049 · server geography" />

        <text className="a-label" x="72" y="112">
          MLX server region
        </text>
        <text className="a-sub" x="72" y="131">
          Choose one · EU or US
        </text>

        <rect className="a-line strong" x="70" y="152" width="690" height="310" rx="3" />
        <text className="a-note" x="94" y="179">
          Geography stamps · compare before selection
        </text>

        <g>
          <rect className="dvr-region-card selected" x="104" y="202" width="286" height="194" rx="2" />
          <rect className="dvr-selection-band" x="104" y="202" width="286" height="25" rx="2" />
          <text className="dvr-selection-text" x="122" y="219">
            Selected study state
          </text>
          <EuFlag x={130} y={254} width={118} height={79} />
          <text className="a-label accent" x="278" y="270">
            EU
          </text>
          <text className="a-body" x="278" y="294">
            Selected
          </text>
          <circle className="dvr-status-dot" cx="284" cy="319" r="4" />
          <text className="a-sub" x="296" y="323">
            Server region
          </text>
        </g>

        <g>
          <rect className="dvr-region-card" x="438" y="202" width="286" height="194" rx="2" />
          <text className="a-note" x="456" y="221">
            Available alternative
          </text>
          <UsFlag x={464} y={254} width={118} height={62} opacity={0.82} />
          <text className="a-label" x="612" y="270">
            US
          </text>
          <text className="a-body" x="612" y="294">
            Available
          </text>
          <circle className="a-node hollow" cx="618" cy="319" r="4" />
          <text className="a-sub" x="630" y="323">
            Server region
          </text>
        </g>

        <path className="a-line faint" d="M104 424 H724" />
        <text className="a-note ink" x="104" y="444">
          Flag scope · location of the MLX server
        </text>

        <g>
          <text className="a-label" x="822" y="156">
            Selection readout
          </text>
          <path className="a-line strong" d="M822 174 H1102" />
          <circle className="dvr-status-dot" cx="837" cy="213" r="5" />
          <text className="a-body" x="858" y="217">
            EU · selected
          </text>
          <path className="a-line faint" d="M822 238 H1102" />
          <circle className="a-node hollow" cx="837" cy="274" r="5" />
          <text className="a-body" x="858" y="278">
            US · available alternative
          </text>
          <path className="a-line faint" d="M822 301 H1102" />
          <text className="a-note" x="822" y="336">
            One server-region selection
          </text>
          <text className="a-note" x="822" y="354">
            Flags do not label provider routes
          </text>
          <Dim x1={822} y1={382} x2={1102} y2={382} label="EU or US · explicit choice" offset={22} accent />
        </g>

        <TitleBlock
          x={70}
          y={520}
          width={1040}
          drawing="MLX-DVR-003 · Region stamps"
          rows={[
            ["Scope", "MLX server region"],
            ["Choice", "EU or US"],
            ["Study state", "EU selected", true],
          ]}
        />
      </Sheet>
    </Plate>
  );
}

function ResidencyBoundaryStudy() {
  return (
    <Plate
      figure="Fig. DVR-004"
      caption="The selected region encloses the MLX server layers. Any external model-provider route remains visibly separate and requires its own approval; the diagram makes no blanket end-to-end residency claim."
      motion={false}
    >
      <style>{REGION_CSS}</style>
      <Sheet
        height={620}
        label="Residency boundary study for DRW-049 showing authoritative third-party sources outside a selected EU MLX server boundary; inside are read-only ingress, the Twin and MLX route policy; a separately approved external model-provider path sits beyond the boundary, with US shown as an available server-region alternative"
      >
        <SheetHeading label="Residency boundary, separated" sub="DVR-004 · Variant of DRW-049 · server region versus provider path" />

        <g>
          <rect className="dvr-zone external" x="64" y="174" width="138" height="190" rx="2" />
          <text className="a-label" x="82" y="201">
            Sources
          </text>
          <text className="a-sub" x="82" y="218">
            Authoritative
          </text>
          {[252, 286, 320].map((y, index) => (
            <g key={`source-socket-${y}`}>
              <circle className="a-ring" cx="100" cy={y} r="10" />
              <circle className="a-node hollow" cx="100" cy={y} r="3.5" />
              <text className="a-note" x="122" y={y + 4}>
                Source {index + 1}
              </text>
            </g>
          ))}
        </g>

        <g>
          <rect className="dvr-boundary" x="238" y="120" width="590" height="334" rx="3" />
          <text className="a-label accent" x="262" y="151">
            MLX server region · EU selected
          </text>
          <EuFlag x={698} y={136} width={92} height={61} />

          <rect className="dvr-zone" x="278" y="211" width="126" height="120" rx="2" />
          <text className="a-label" x="298" y="238">
            Ingress
          </text>
          <text className="a-sub" x="298" y="257">
            Read-only
          </text>
          <path className="a-line strong" d="M307 284 H375 M341 272 V296" />
          <text className="a-note" x="298" y="316">
            Scoped fitting
          </text>

          <rect className="dvr-twin-shell" x="443" y="190" width="192" height="162" rx="2" />
          <text className="dvr-twin-title" x="539" y="235" textAnchor="middle">
            The Twin
          </text>
          <path d="M475 259 H603" stroke="var(--ground)" strokeOpacity=".62" />
          <text className="dvr-twin-copy" x="539" y="282" textAnchor="middle">
            Approved · versioned
          </text>
          <text className="dvr-twin-copy" x="539" y="303" textAnchor="middle">
            Evidence-linked context
          </text>
          <circle cx="539" cy="326" r="5" fill="var(--ground)" />

          <rect className="dvr-zone" x="674" y="211" width="112" height="120" rx="2" />
          <text className="a-label" x="694" y="238">
            Route
          </text>
          <text className="a-sub" x="694" y="257">
            MLX policy
          </text>
          <path className="a-line strong" d="M704 282 H758 M731 270 V294" />
          <text className="a-note" x="694" y="316">
            Approved access
          </text>
        </g>

        <Arrow d="M202 271 H278" />
        <Arrow d="M404 271 H443" accent />
        <Arrow d="M635 271 H674" accent />
        <Arrow d="M786 271 H884" />

        <g>
          <rect className="dvr-zone external" x="884" y="174" width="228" height="190" rx="2" />
          <text className="a-label" x="906" y="201">
            External path
          </text>
          <text className="a-sub" x="906" y="220">
            Separately approved
          </text>
          {["Model A", "Model B"].map((model, index) => (
            <g key={model}>
              <circle className="a-ring" cx="923" cy={266 + index * 50} r="11" />
              <circle className="a-node hollow" cx="923" cy={266 + index * 50} r="3.5" />
              <text className="a-body" x="948" y={270 + index * 50}>
                {model}
              </text>
            </g>
          ))}
        </g>

        <g>
          <rect className="dvr-region-card" x="884" y="392" width="228" height="70" rx="2" />
          <UsFlag x={900} y={407} width={75} height={39.5} opacity={0.8} />
          <text className="a-label" x="994" y="421">
            US
          </text>
          <text className="a-sub" x="994" y="442">
            Available alternative
          </text>
        </g>

        <text className="a-note ink" x="64" y="490">
          Server region → location of the MLX server
        </text>
        <text className="a-note ink" x="620" y="490">
          Provider path → separately approved external route
        </text>

        <TitleBlock
          x={64}
          y={520}
          width={1048}
          drawing="MLX-DVR-004 · Residency boundary"
          rows={[
            ["Server region", "EU selected", true],
            ["Alternative", "US available"],
            ["Provider path", "Separately approved"],
          ]}
        />
      </Sheet>
    </Plate>
  );
}

export const REGION_DRAWING_VARIANTS: ConceptSpec[] = [
  {
    id: "DVR-003",
    title: "Region stamp selection",
    note: "Makes the EU or US MLX server-region choice visual without using a flag as a blanket residency claim.",
    surface: "ground",
    width: "contained",
    interaction: "static",
    variantOf: "DRW-049",
    state: "Region stamps",
    render: () => <RegionStampStudy />,
  },
  {
    id: "DVR-004",
    title: "Residency boundary, separated",
    note: "Separates the selected server region from a separately approved external model-provider path.",
    surface: "ground",
    width: "contained",
    interaction: "static",
    variantOf: "DRW-049",
    state: "Residency boundary",
    render: () => <ResidencyBoundaryStudy />,
  },
];
